// src/features/MyGymTV/UK/UKDashboard/UKDashboard.js
import React, { useMemo, useCallback } from 'react';
import { Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedClubs } from '../../../../redux/slices/myGymTV/ukEntitiesSlice';
import Toast, { useToast } from '../../../../features/Toast/Toast';

const UKDashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { selectedClubs } = useSelector((state) => state.ukEntities);

    // Toast state
    const { toastState, showToast, hideToast } = useToast();

    const hasSelection = selectedClubs.length > 0;

    // Determine button visibility based on selected clubs' capabilities
    // Uses HasD6/HasDS flags that come with the entity data (no API call needed)
    const { showD6Button, showDSButton } = useMemo(() => {
        if (selectedClubs.length === 0) {
            return { showD6Button: false, showDSButton: false };
        }

        const anyHasD6 = selectedClubs.some(club => club.HasD6);
        const anyHasDS = selectedClubs.some(club => club.HasDS);
        const noneHaveEither = !anyHasD6 && !anyHasDS;

        // Show both if none have either (edge case), otherwise show based on capability
        return {
            showD6Button: anyHasD6 || noneHaveEither,
            showDSButton: anyHasDS || noneHaveEither
        };
    }, [selectedClubs]);

    // Handle Add D6 button click - filter to D6-capable clubs
    const handleAddD6Click = useCallback(() => {
        const d6Clubs = selectedClubs.filter(club => club.HasD6);
        const totalCount = selectedClubs.length;
        const d6Count = d6Clubs.length;
        const removedCount = totalCount - d6Count;

        if (d6Count === 0) {
            showToast('None of the selected venues have D6 players. Please select different venues.', 'warning');
            return;
        }

        if (removedCount > 0) {
            dispatch(setSelectedClubs(d6Clubs));
            showToast(
                `${d6Count} of ${totalCount} selected venues have D6 players. ${removedCount} DS-only venue${removedCount > 1 ? 's have' : ' has'} been removed from selection.`,
                'info'
            );
            // Delay navigation so user can see the toast
            setTimeout(() => navigate('/uk/add-d6'), 3000);
        } else {
            navigate('/uk/add-d6');
        }
    }, [selectedClubs, dispatch, navigate, showToast]);

    // Handle Add DS button click - filter to DS-capable clubs
    const handleAddDSClick = useCallback(() => {
        const dsClubs = selectedClubs.filter(club => club.HasDS);
        const totalCount = selectedClubs.length;
        const dsCount = dsClubs.length;
        const removedCount = totalCount - dsCount;

        if (dsCount === 0) {
            showToast('None of the selected venues have DS players. Please select different venues.', 'warning');
            return;
        }

        if (removedCount > 0) {
            dispatch(setSelectedClubs(dsClubs));
            showToast(
                `${dsCount} of ${totalCount} selected venues have DS players. ${removedCount} D6-only venue${removedCount > 1 ? 's have' : ' has'} been removed from selection.`,
                'info'
            );
            setTimeout(() => navigate('/uk/add-ds'), 3000);
        } else {
            navigate('/uk/add-ds');
        }
    }, [selectedClubs, dispatch, navigate, showToast]);

    // Handle Review D6 button click - just navigate, no filtering needed
    const handleReviewD6Click = useCallback(() => {
        navigate('/uk/review-d6');
    }, [navigate]);

    // Handle Review DS button click - just navigate, no filtering needed
    const handleReviewDSClick = useCallback(() => {
        navigate('/uk/review-ds');
    }, [navigate]);

    if (!hasSelection) {
        return (
            <>
                <Toast
                    duration={5000}
                    message={toastState.message}
                    onClose={hideToast}
                    show={toastState.show}
                    variant={toastState.variant}
                />
                <Card className="mt-5 p-3 text-center">
                    <Card.Title>Welcome to MyGymTV UK</Card.Title>
                    <Card.Body>
                        <p>Please select one or more venues from the menu on the left to continue.</p>
                    </Card.Body>
                </Card>
            </>
        );
    }

    return (
        <>
            <Toast
                duration={5000}
                message={toastState.message}
                onClose={hideToast}
                show={toastState.show}
                variant={toastState.variant}
            />
            <div className="uk-dashboard mt-5">
                <div className="row">
                    <div className="col-md-6">
                        <Card className="p-3 text-center h-100">
                            <Card.Title>Add Content</Card.Title>
                            <Card.Body className="d-flex flex-column justify-content-center">
                                <p className="mb-4">Upload new content for your venues</p>
                                <div className="d-flex flex-column gap-3">
                                    {showDSButton && (
                                        <Button
                                            onClick={handleAddDSClick}
                                            variant="primary"
                                        >
                                            Add DS Content
                                        </Button>
                                    )}
                                    {showD6Button && (
                                        <Button
                                            onClick={handleAddD6Click}
                                            variant="primary"
                                        >
                                            Add D6 Content
                                        </Button>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </div>

                    <div className="col-md-6">
                        <Card className="p-3 text-center h-100">
                            <Card.Title>Review Content</Card.Title>
                            <Card.Body className="d-flex flex-column justify-content-center">
                                <p className="mb-4">View and manage existing content</p>
                                <div className="d-flex flex-column gap-3">
                                    {showDSButton && (
                                        <Button
                                            onClick={handleReviewDSClick}
                                            variant="secondary"
                                        >
                                            Review DS Content
                                        </Button>
                                    )}
                                    {showD6Button && (
                                        <Button
                                            onClick={handleReviewD6Click}
                                            variant="secondary"
                                        >
                                            Review D6 Content
                                        </Button>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UKDashboard;